<template>
  <select
    ref="select"
    :class="getClass"
    :multiple="multiple"
    :name="name"
    :placeholder="getPlaceholder"
    :value="getValue"
    @blur="onBlur"
    @change="onInput"
    @focus="onFocus"
    @keydown.enter="onEnter">

    <template v-if="getPlaceholder">
      <option :disabled="!noDisabledPlaceholder" selected :value="null">{{ getPlaceholder }}</option>
    </template>

    <template v-for="({ text, value }, key) in selectable">
      <option :key="key" :selected="isSelected(value)" :value="value">{{ text }}</option>
    </template>

    <slot />

  </select>
</template>

<script>
import { FormElement } from '@/mixins'

export default {
  name: 'BaseSelect',

  mixins: [FormElement],

  props: {
    blank: String,
    model: Array,
    modelText: { default: 'name', type: String },
    modelTextFormat: Function,
    modelValue: { default: '_id', type: String },
    noDisabledPlaceholder: Boolean,
    multiple: Boolean,
    options: Array,
    source: String,
    store: String,
    storeAction: String,
    storeText: { default: 'name', type: String },
    storeTextFormat: Function,
    storeValue: { default: '_id', type: String },
    truthy: Boolean
  },

  data: () => ({
    selectable: []
  }),

  created () {
    this.getOptions()
  },

  methods: {
    getFromModel () {
      const options = []

      this.model.forEach(model => options.push({
        text: this.$_.isFunction(this.modelTextFormat) ? this.modelTextFormat(model) : this.$_.get(model, this.modelText),
        value: this.$_.get(model, this.modelValue)
      }))

      return options
    },

    getFromSource () {
      return require(`../data/${this.source}.js`).default
    },

    async getFromStore () {
      return new Promise(async (resolve, reject) => {
        // Do we need to dispatch an action first?
        if (this.storeAction) {
          await this.$store.dispatch(this.storeAction).catch(err => reject(err))
        }

        const state = this.$_.get(this.$store.state, this.store)
        const options = []

        if (this.$_.isArray(state)) {
          state.forEach(entity => options.push({
            text: this.$_.isFunction(this.storeTextFormat) ? this.storeTextFormat(entity) : this.$_.get(entity, this.storeText),
            value: this.$_.get(entity, this.storeValue)
          }))
        }

        resolve(options)
      })
    },

    getTruthySelection () {
      return [{
        text: 'Yes',
        value: true
      }, {
        text: 'No',
        value: false
      }]
    },

    async getOptions () {
      if (this.source) {
        this.selectable = this.getFromSource()
      }

      if (this.model) {
        this.selectable = this.getFromModel()
      }

      if (this.store) {
        await this.getFromStore()
          .then(options => {
            this.selectable = options
          })
      }

      if (this.truthy) {
        this.selectable = this.getTruthySelection()
      }

      if (this.options && this.options.length) {
        this.selectable = this.options
      }

      if (this.blank) {
        this.selectable.unshift({
          text: this.blank,
          value: ''
        })
      }
    },

    getSelectedOptions () {
      const selected = []

      this.$refs.select.querySelectorAll('option').forEach(option => {
        if (option.selected) {
          selected.push(option.value)
        }
      })

      return selected
    },

    isSelected (value) {
      return this.multiple ? this.value.includes(value) : this.value === value
    },

    onInput ($event) {
      this.$nextTick(() => {
        if ($event.target) {
          this.getValue = this.multiple ? this.getSelectedOptions() : $event.target.value
        }
      })
    }
  },

  watch: {
    model () {
      this.selectable = this.getFromModel()
    },

    options () {
      if (this.options && this.options.length) {
        this.selectable = this.options
      }
    }
  }
}
</script>
